// The logged-in person and the Library's rows, shared by every logged-in screen (the header's search
// and bell need them on every page). Who they are comes from hub.whoami() and their own people row.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { supabase, must } from '../lib/supabase.js'
import { loadLibrary, loadLevels, hideAnnouncement, markNotificationsRead } from '../library/load.js'

// 2 sets of tile levels ({ product id: level key }) with the same keys and values
const sameLevels = (a = {}, b = {}) => {
  const ka = Object.keys(a || {}), kb = Object.keys(b || {})
  return ka.length === kb.length && ka.every(k => a[k] === b[k])
}

const MemberContext = createContext(null)
export const useMember = () => useContext(MemberContext)

export function MemberProvider({ session, children }) {
  const userId = session.user.id
  const [me, setMe] = useState({ status: 'loading' })
  const [lib, setLib] = useState({ status: 'idle', data: null })
  const loading = useRef(null)
  const loads = useRef(0)

  const loadMe = useCallback(async () => {
    try {
      const who = (await supabase.rpc('whoami').then(must))?.[0] || null
      if (!who?.acting_person_id) { setMe({ status: 'none' }); return null }
      const person = await supabase.from('people').select('id, first_name, full_name, email, role')
        .eq('id', who.acting_person_id).single().then(must)
      const next = { status: 'ready', who, person }
      setMe(next)
      return next
    } catch (error) {
      setMe({ status: 'error', error })
      return null
    }
  }, [])

  useEffect(() => { setMe({ status: 'loading' }); setLib({ status: 'idle', data: null }); loadMe() }, [userId, loadMe])

  // Who the Library's rows are for: the acting person, whether staff are viewing as them, and the staff
  // role (whoami fills it only at 2-step login, which opens more of the catalogue). When it changes (a
  // view as starts or stops, or 2-step login is passed) the Library loaded before is someone else's or
  // out of date, so it's loaded again; a load still on its way for the old key is dropped.
  const actingKey = me.status === 'ready' ? `${me.person.id}|${me.who?.viewing_as ? 'as' : ''}|${me.who?.staff_role || ''}` : ''
  const keyRef = useRef(actingKey)
  keyRef.current = actingKey

  const refreshLibrary = useCallback(async () => {
    if (me.status !== 'ready') return
    if (loading.current?.key === actingKey) return loading.current.promise
    const key = actingKey
    setLib(l => ({ ...l, status: l.data ? 'ready' : 'loading' }))
    const promise = loadLibrary(me.person.id)
      .then(({ balancesLate, ...data }) => {
        if (keyRef.current !== key) return
        // loadId: which load this is, for the Library's own read of the tiles' levels (refreshLevels); a mastermind
        // tile's level stays as it was while it's read again, so it doesn't blink on every visit
        const loadId = ++loads.current
        setLib(l => ({ status: 'ready', data: { ...data, loadId, levels: l.data?.personId === data.personId ? l.data.levels || {} : {} } }))
        // a secured place's amounts from Stripe, when they came after the Library had shown (library/load.js):
        // put in once they come, while the Library is still this person's and no newer load has put them in
        balancesLate?.then(balances => {
          if (keyRef.current !== key || !balances?.length) return
          setLib(l => (l.data?.personId === data.personId && !l.data.balances.length ? { ...l, data: { ...l.data, balances } } : l))
        })
      })
      .catch(error => { if (keyRef.current === key) setLib(l => (l.data ? l : { status: 'error', data: null, error })) })
      .finally(() => { if (loading.current?.key === key) loading.current = null })
    loading.current = { key, promise }
    return promise
  }, [me, actingKey])

  // Where they stand on a mastermind's tile (library/levels.js, load.js loadLevels), for the Library's own visit only
  // (screens/Library.jsx), never a header's load of the same rows, so a product page opened first reads no log more:
  // read once the Library has shown, never waited on, and put in when it comes within its wait, while the Library is
  // still this person's. A read that fails or is slow leaves the tile without it.
  const refreshLevels = useCallback(data => {
    if (!data) return
    const key = keyRef.current
    loadLevels(data).then(levels => {
      if (keyRef.current !== key) return
      // the same levels as the tiles have (nobody with a mastermind, or nothing changed): the Library stays as it is,
      // with no second build of its model and no redraw
      setLib(l => (l.data?.personId === data.personId && !sameLevels(l.data.levels, levels) ? { ...l, data: { ...l.data, levels } } : l))
    })
  }, [])

  const shownKey = useRef(actingKey)
  useEffect(() => {
    if (!actingKey || shownKey.current === actingKey) return
    const before = shownKey.current
    shownKey.current = actingKey
    // the first load after logging in is the pages' own (ensureLibrary, the Library's visit)
    if (!before) return
    setLib(l => (l.status === 'idle' ? l : { status: 'loading', data: null }))
    if (lib.status !== 'idle') refreshLibrary()
  }, [actingKey, refreshLibrary, lib.status])

  // Header-only pages load it once; the Library refreshes it on every visit
  const ensureLibrary = useCallback(() => { if (lib.status === 'idle') refreshLibrary() }, [lib.status, refreshLibrary])

  // While staff view as a member nothing is written (lib/viewAs.jsx): hiding the announcement and
  // opening the bell change nothing, on every page that offers them
  const viewingAs = me.status === 'ready' && Boolean(me.who?.viewing_as)

  const hide = useCallback(id => {
    if (viewingAs) return
    setLib(l => (l.data ? { ...l, data: { ...l.data, hides: [...l.data.hides, { announcement_id: id }] } } : l))
    hideAnnouncement(me.person.id, id).catch(() => {})
  }, [me, viewingAs])

  const markRead = useCallback(() => {
    if (viewingAs) return
    if (!lib.data || !lib.data.notifications.some(n => !n.read_at)) return
    const at = new Date().toISOString()
    setLib(l => ({ ...l, data: { ...l.data, notifications: l.data.notifications.map(n => (n.read_at ? n : { ...n, read_at: at })) } }))
    markNotificationsRead(me.person.id).catch(() => {})
  }, [lib.data, me, viewingAs])

  return (
    <MemberContext.Provider value={{ session, me, loadMe, lib, refreshLibrary, refreshLevels, ensureLibrary, hide, markRead }}>
      {children}
    </MemberContext.Provider>
  )
}
